<template>
  <div class="ai-chat" :class="{ 'ai-chat--empty': !hasConversation }">
    <!-- Top bar -->
    <header class="ai-chat__header">
      <div class="ai-chat__header-inner">
        <div class="d-flex align-center">
          <div class="ai-chat__logo mr-2">B</div>
          <span class="ai-chat__title">AI-BOS</span>
          <v-chip x-small :color="connected ? 'success' : 'error'" dark class="ml-2">
            {{ connected ? 'Online' : 'Offline' }}
          </v-chip>
        </div>
        <div class="d-flex align-center">
          <span class="text-caption grey--text mr-3">{{ currentStore }}</span>
          <v-btn icon small @click="clearChat" :disabled="isLoading">
            <v-icon small>mdi-plus</v-icon>
          </v-btn>
        </div>
      </div>
    </header>

    <!-- Messages area -->
    <main ref="messagesContainer" class="ai-chat__messages">
      <!-- Welcome screen when no messages -->
      <div v-if="!hasConversation" class="ai-chat__welcome">
        <div class="ai-chat__welcome-logo">B</div>
        <h1 class="ai-chat__welcome-title">¿En qué puedo ayudarte?</h1>
        <p class="ai-chat__welcome-sub">
          Soy tu analista de retail con acceso a ventas, inventario, rentabilidad y más.
        </p>
        <div class="ai-chat__suggestions">
          <button
            v-for="s in welcomeSuggestions"
            :key="s.text"
            class="ai-chat__suggestion"
            @click="useSuggestion(s.text)"
            :disabled="isLoading"
          >
            <v-icon small class="mr-2" color="grey darken-1">{{ s.icon }}</v-icon>
            <span>{{ s.text }}</span>
          </button>
        </div>
      </div>

      <!-- Conversation messages -->
      <div v-else class="ai-chat__conversation">
        <template v-for="(msg, i) in conversation">
          <!-- User message -->
          <div v-if="msg.role === 'user'" :key="'u'+i" class="ai-chat__msg ai-chat__msg--user">
            <div class="ai-chat__msg-content">
              <div class="ai-chat__msg-text">{{ msg.content }}</div>
            </div>
          </div>

          <!-- AI message -->
          <div v-else :key="'a'+i" class="ai-chat__msg ai-chat__msg--ai">
            <div class="ai-chat__msg-avatar">B</div>
            <div class="ai-chat__msg-content">
              <div
                class="ai-chat__msg-text ai-chat__markdown"
                v-html="renderMarkdown(msg.content)"
              ></div>
              <div class="ai-chat__msg-actions">
                <button @click="copyMessage(msg.content)" title="Copiar">
                  <v-icon x-small>mdi-content-copy</v-icon>
                </button>
              </div>
            </div>
          </div>
        </template>

        <!-- Loading indicator -->
        <div v-if="isLoading" class="ai-chat__msg ai-chat__msg--ai">
          <div class="ai-chat__msg-avatar ai-chat__msg-avatar--loading">B</div>
          <div class="ai-chat__msg-content">
            <div class="ai-chat__thinking">
              <span class="ai-chat__dot"></span>
              <span class="ai-chat__dot"></span>
              <span class="ai-chat__dot"></span>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Input area -->
    <footer class="ai-chat__footer">
      <div class="ai-chat__input-wrap">
        <textarea
          ref="inputEl"
          v-model="input"
          :placeholder="inputPlaceholder"
          :disabled="isLoading || !connected"
          @keydown="handleKeydown"
          @input="autoResize"
          rows="1"
          class="ai-chat__input"
        ></textarea>
        <button
          class="ai-chat__send"
          :class="{ 'ai-chat__send--active': canSend }"
          :disabled="!canSend"
          @click="handleSubmit"
        >
          <v-icon small :color="canSend ? 'white' : 'grey'">mdi-arrow-up</v-icon>
        </button>
      </div>
      <div class="ai-chat__disclaimer">
        AI-BOS puede cometer errores. Verifica la información importante.
      </div>
    </footer>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" top right>
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import { webserver, getToday, formatCurrency } from "@/services/webserver.js";
import { marked } from "marked";
import DOMPurify from "dompurify";

export default {
  name: "ClaudeAiAssistant",

  data() {
    return {
      connected: false,
      isLoading: false,
      currentStore: null,
      input: "",
      conversation: [],
      snackbar: { show: false, text: "", color: "info" },
      welcomeSuggestions: [
        { icon: "mdi-pulse", text: "¿Cómo van las ventas hoy?" },
        { icon: "mdi-chart-timeline-variant", text: "Analiza las ventas de este mes vs el anterior" },
        { icon: "mdi-package-variant", text: "¿Cómo está la salud del inventario?" },
        { icon: "mdi-cash-multiple", text: "Dame un análisis de rentabilidad" },
        { icon: "mdi-account-group", text: "¿Cuál es el ranking de vendedores?" },
        { icon: "mdi-tag-multiple", text: "¿Qué categorías tienen más oportunidad?" },
      ],
    };
  },

  computed: {
    hasConversation() {
      return this.conversation.length > 0;
    },
    canSend() {
      return this.input.trim().length > 0 && !this.isLoading && this.connected;
    },
    inputPlaceholder() {
      return this.connected
        ? "Escribe tu pregunta..."
        : "Conectando...";
    },
  },

  async mounted() {
    this.initializeStore();
    await this.testConnection();
    this.$refs.inputEl?.focus();
  },

  methods: {
    // === INIT ===
    initializeStore() {
      if (window.store && window.store.store_id) {
        this.currentStore = window.store.store_id;
      } else if (this.$store?.state?.profile?.store_id) {
        this.currentStore = this.$store.state.profile.store_id;
      } else if (localStorage.getItem("current_store")) {
        this.currentStore = localStorage.getItem("current_store");
      } else {
        this.currentStore = "462401000";
      }
    },

    async testConnection() {
      try {
        await this.callWebserver("test", { store: this.currentStore });
        this.connected = true;
      } catch {
        this.connected = false;
      }
    },

    // === API ===
    async callWebserver(endpoint, data) {
      const cleanData = JSON.parse(JSON.stringify(data));
      return new Promise((resolve, reject) => {
        webserver(endpoint, cleanData, (result) => {
          if (result === undefined || result === null) {
            reject(new Error(`Empty response from ${endpoint}`));
          } else {
            resolve(result);
          }
        });
        setTimeout(() => reject(new Error("Timeout")), 90000);
      });
    },

    // === MESSAGING ===
    async handleSubmit() {
      if (!this.canSend) return;
      const text = this.input.trim();
      this.input = "";
      this.resetTextarea();
      await this.processMessage(text);
    },

    handleKeydown(e) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        this.handleSubmit();
      }
    },

    async processMessage(text) {
      this.conversation.push({ role: "user", content: text });
      this.scrollToBottom();

      this.isLoading = true;
      try {
        const result = await this.callWebserver("chat_message", {
          store: this.currentStore,
          query: text,
          context: "general",
        });

        const response = this.extractResponse(result);
        this.conversation.push({ role: "assistant", content: response });
      } catch (error) {
        this.conversation.push({
          role: "assistant",
          content: `**Error:** ${error.message}\n\nIntenta reformular tu pregunta o verifica la conexión.`,
        });
      } finally {
        this.isLoading = false;
        this.scrollToBottom();
      }
    },

    extractResponse(response) {
      if (!response) return "Sin respuesta del servidor.";
      if (typeof response === "string") return response;
      if (response.response) return response.response;

      const fields = ["claude_response", "analysis", "answer", "result", "message", "data"];
      for (const f of fields) {
        if (response[f] && typeof response[f] === "string") return response[f];
      }

      if (response.success === false && response.error) {
        return `**Error:** ${response.error}`;
      }

      return JSON.stringify(response, null, 2);
    },

    useSuggestion(text) {
      this.input = text;
      this.$nextTick(() => this.handleSubmit());
    },

    // === UI ===
    clearChat() {
      this.conversation = [];
      this.input = "";
      this.resetTextarea();
      this.$nextTick(() => this.$refs.inputEl?.focus());
    },

    copyMessage(content) {
      navigator.clipboard.writeText(content).then(() => {
        this.snackbar = { show: true, text: "Copiado al portapapeles", color: "success" };
      });
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messagesContainer;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    autoResize() {
      const el = this.$refs.inputEl;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 200) + "px";
    },

    resetTextarea() {
      this.$nextTick(() => {
        const el = this.$refs.inputEl;
        if (el) el.style.height = "auto";
      });
    },

    renderMarkdown(content) {
      if (!content) return "";

      const renderer = new marked.Renderer();

      renderer.heading = (text, level) => {
        const sizes = { 1: '1.25rem', 2: '1.15rem', 3: '1.05rem' };
        return `<h${level} style="font-size:${sizes[level] || '1rem'};font-weight:600;margin:1.2em 0 0.5em;color:#1a1a1a;">${text}</h${level}>`;
      };

      renderer.paragraph = (text) => `<p style="margin:0 0 0.8em;line-height:1.65;">${text}</p>`;

      renderer.strong = (text) => `<strong style="font-weight:600;color:#1a1a1a;">${text}</strong>`;

      renderer.list = (body, ordered) => {
        const tag = ordered ? "ol" : "ul";
        return `<${tag} style="margin:0 0 1em;padding-left:1.5em;">${body}</${tag}>`;
      };

      renderer.listitem = (text) => `<li style="margin:0.25em 0;line-height:1.6;">${text}</li>`;

      renderer.table = (header, body) =>
        `<div style="overflow-x:auto;margin:1em 0;"><table style="width:100%;border-collapse:collapse;font-size:0.875rem;">${header}${body}</table></div>`;

      renderer.tablerow = (content) => `<tr style="border-bottom:1px solid #e5e5e5;">${content}</tr>`;

      renderer.tablecell = (content, flags) => {
        const tag = flags.header ? "th" : "td";
        const style = flags.header
          ? "padding:8px 12px;text-align:left;font-weight:600;background:#f5f5f5;color:#333;white-space:nowrap;"
          : "padding:8px 12px;text-align:left;";
        return `<${tag} style="${style}">${content}</${tag}>`;
      };

      renderer.code = (code) =>
        `<pre style="background:#f7f7f8;border-radius:8px;padding:12px 16px;overflow-x:auto;font-size:0.85rem;margin:1em 0;"><code>${code}</code></pre>`;

      renderer.codespan = (code) =>
        `<code style="background:#f0f0f0;padding:2px 6px;border-radius:4px;font-size:0.875em;">${code}</code>`;

      renderer.blockquote = (quote) =>
        `<blockquote style="border-left:3px solid #d1d5db;padding-left:1em;margin:1em 0;color:#555;">${quote}</blockquote>`;

      renderer.link = (href, title, text) =>
        `<a href="${href}" ${title ? `title="${title}"` : ""} target="_blank" rel="noopener" style="color:#2563eb;text-decoration:none;">${text}</a>`;

      marked.setOptions({ renderer, gfm: true, breaks: true });

      const html = marked(content);
      return DOMPurify.sanitize(html, {
        ALLOWED_ATTR: ["class", "style", "href", "title", "target", "rel"],
        ALLOWED_TAGS: [
          "div", "p", "h1", "h2", "h3", "h4", "h5", "h6",
          "strong", "em", "ul", "ol", "li", "pre", "code",
          "span", "a", "br", "table", "thead", "tbody", "tr", "th", "td",
          "blockquote", "hr", "img",
        ],
      });
    },

    getToday,
    formatCurrency,
  },
};
</script>

<style lang="scss" scoped>
// === Layout ===
.ai-chat {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #ffffff;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #1a1a1a;
}

// === Header ===
.ai-chat__header {
  flex-shrink: 0;
  border-bottom: 1px solid #e8e8e8;
  background: #fff;
}

.ai-chat__header-inner {
  max-width: 800px;
  margin: 0 auto;
  padding: 12px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ai-chat__logo {
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #D97706, #B45309);
  color: #fff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
}

.ai-chat__title {
  font-weight: 600;
  font-size: 15px;
  color: #333;
}

// === Messages area ===
.ai-chat__messages {
  flex: 1;
  overflow-y: auto;
  scroll-behavior: smooth;
}

// === Welcome screen ===
.ai-chat__welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: 40px 24px;
}

.ai-chat__welcome-logo {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #D97706, #B45309);
  color: #fff;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 28px;
  margin-bottom: 24px;
}

.ai-chat__welcome-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 8px;
}

.ai-chat__welcome-sub {
  font-size: 0.95rem;
  color: #666;
  margin: 0 0 32px;
  text-align: center;
  max-width: 480px;
}

.ai-chat__suggestions {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  max-width: 600px;
  width: 100%;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.ai-chat__suggestion {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  font-size: 0.875rem;
  color: #333;
  transition: all 0.15s ease;

  &:hover {
    background: #f9f9f9;
    border-color: #bbb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// === Conversation ===
.ai-chat__conversation {
  max-width: 800px;
  margin: 0 auto;
  padding: 24px 24px 100px;
  width: 100%;
}

// === Messages ===
.ai-chat__msg {
  margin-bottom: 24px;
  display: flex;
  gap: 12px;
}

.ai-chat__msg--user {
  justify-content: flex-end;

  .ai-chat__msg-content {
    max-width: 75%;
  }

  .ai-chat__msg-text {
    background: #f0f0f0;
    padding: 12px 16px;
    border-radius: 20px 20px 4px 20px;
    font-size: 0.9375rem;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
  }
}

.ai-chat__msg--ai {
  .ai-chat__msg-content {
    flex: 1;
    min-width: 0;
  }

  .ai-chat__msg-text {
    font-size: 0.9375rem;
    line-height: 1.65;
    color: #1a1a1a;
  }
}

.ai-chat__msg-avatar {
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #D97706, #B45309);
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
  flex-shrink: 0;
  margin-top: 2px;
}

.ai-chat__msg-actions {
  margin-top: 8px;
  opacity: 0;
  transition: opacity 0.15s;

  .ai-chat__msg:hover & {
    opacity: 1;
  }

  button {
    background: none;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 4px 8px;
    cursor: pointer;
    color: #888;
    transition: all 0.15s;

    &:hover {
      background: #f5f5f5;
      color: #333;
    }
  }
}

// === Loading dots ===
.ai-chat__thinking {
  display: flex;
  gap: 4px;
  padding: 8px 0;
}

.ai-chat__dot {
  width: 8px;
  height: 8px;
  background: #ccc;
  border-radius: 50%;
  animation: dotPulse 1.4s ease-in-out infinite;

  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.4s; }
}

@keyframes dotPulse {
  0%, 80%, 100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
}

// === Footer / Input ===
.ai-chat__footer {
  flex-shrink: 0;
  padding: 0 24px 16px;
  background: #fff;

  .ai-chat--empty & {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 0 24px 16px;
  }
}

.ai-chat__input-wrap {
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  background: #f7f7f8;
  border: 1px solid #e0e0e0;
  border-radius: 24px;
  padding: 8px 48px 8px 20px;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus-within {
    border-color: #bbb;
    box-shadow: 0 0 0 2px rgba(0,0,0,0.05);
  }
}

.ai-chat__input {
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: #1a1a1a;
  max-height: 200px;
  font-family: inherit;
  padding: 4px 0;

  &::placeholder {
    color: #999;
  }

  &:disabled {
    opacity: 0.5;
  }
}

.ai-chat__send {
  position: absolute;
  right: 8px;
  bottom: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #e0e0e0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;

  &--active {
    background: #1a1a1a;
    cursor: pointer;

    &:hover {
      background: #333;
    }
  }

  &:disabled {
    cursor: not-allowed;
  }
}

.ai-chat__disclaimer {
  text-align: center;
  font-size: 0.75rem;
  color: #999;
  margin-top: 8px;
}

// === Markdown table improvements ===
.ai-chat__markdown {
  ::v-deep table {
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    overflow: hidden;

    th {
      background: #f5f5f5 !important;
    }

    tr:last-child {
      border-bottom: none;
    }
  }

  ::v-deep pre {
    border: 1px solid #e8e8e8;
  }

  ::v-deep blockquote {
    border-left: 3px solid #D97706;
  }

  ::v-deep a:hover {
    text-decoration: underline;
  }

  ::v-deep hr {
    border: none;
    border-top: 1px solid #e8e8e8;
    margin: 1.5em 0;
  }
}
</style>
